import { useDesktopAutoFocus } from '@lib/hooks/sizing/useDesktopAutoFocus';
import formCss from '@lib/style/form.module.scss';
import { showToast } from '@lib/utils/toastmaster';
import { extractObjectId } from '@lib/utils/url';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import { ActionButton } from '@pages/ActionButton';
import { BaseModal } from '@pages/Search/components/MomentGrid/components/BaseModal/BaseModal';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { useModalRegistration } from '@stores/ModalStore';
import { trackingStore } from '@stores/Tracking';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { FormEvent, forwardRef, useId, useImperativeHandle, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import css from './LibraryDeleteModal.module.scss';
import shell from './LibraryModalShell.module.scss';
import { LibraryRemoval, libraryMediaPhrase, libraryRemovalFor } from './libraryRemoval';

export type LibraryDeleteModalRef = {
  open: () => void;
  close: () => void;
};

/** Typed to arm a permanent delete. A fixed word rather than the library's name, which can be
 * long, blank, or full of characters that are awkward to type. */
const CONFIRMATION_WORD = 'delete';

type LibraryDeleteFormProps = {
  removal: LibraryRemoval;
  currentName: string;
  mediaCount: number | undefined;
  ownerName: string | undefined;
  isDeleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * The modal body. BaseModal unmounts it on close, so the typed confirmation is discarded with
 * it — nothing typed for one library can pre-arm the button for the next.
 */
const LibraryDeleteForm = ({
  removal,
  currentName,
  mediaCount,
  ownerName,
  isDeleting,
  onConfirm,
  onCancel,
}: LibraryDeleteFormProps) => {
  const [typed, setTyped] = useState('');
  const inputId = useId();
  const autoFocusOnDesktop = useDesktopAutoFocus();

  const armed = removal === 'detach' || typed.trim().toLowerCase() === CONFIRMATION_WORD;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!armed) {
      // The submit button is disabled until the input matches, and implicit submission
      // (Enter in the field) skips a disabled default button, so this only fires if the
      // DOM was tampered with.
      throw new Error('Library delete submitted without a matching confirmation');
    }
    onConfirm();
  };

  return (
    <form className={shell.form} onSubmit={handleSubmit}>
      {removal === 'delete' ? (
        <>
          <div className={formCss.warningBox} role="alert">
            <WarningAmberRounded className={formCss.warningIcon} />
            <div className={formCss.warningBody}>
              <span className={formCss.warningTitle}>
                This library and its media will be permanently deleted.
              </span>
              <span>
                {libraryMediaPhrase(mediaCount)} in "{currentName}" will be removed. This cannot be
                undone.
              </span>
            </div>
          </div>
          <div className={shell.field}>
            <label htmlFor={inputId} className={shell.fieldLabel}>
              Type <span className={css.confirmationWord}>{CONFIRMATION_WORD}</span> to confirm
            </label>
            <input
              type="text"
              id={inputId}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={CONFIRMATION_WORD}
              className={css.input}
              disabled={isDeleting}
              autoFocus={autoFocusOnDesktop}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
            />
          </div>
        </>
      ) : (
        <p className={css.detachNotice}>
          "{currentName}" will be removed from your libraries. Its media isn't deleted:{' '}
          {ownerName ? `${ownerName} still owns it.` : 'its owner keeps it.'}
        </p>
      )}
      <div className={shell.actions}>
        <ActionButton variant="secondary" type="button" onClick={onCancel} disabled={isDeleting}>
          Cancel
        </ActionButton>
        <ActionButton variant="danger" type="submit" disabled={!armed || isDeleting}>
          {removal === 'delete'
            ? isDeleting
              ? 'Deleting…'
              : 'Delete library'
            : isDeleting
              ? 'Removing…'
              : 'Remove library'}
        </ActionButton>
      </div>
    </form>
  );
};

export const LibraryDeleteModal = observer(
  forwardRef<
    LibraryDeleteModalRef,
    {
      libraryId: string;
      currentName: string;
    }
  >(({ libraryId, currentName }, ref) => {
    const [open, setOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const libraryManager = useLibraryManager();
    const navigateTo = useNavigate();
    const location = useLocation();

    useModalRegistration('library-delete', open);

    const library = libraryManager.libraryById(libraryId);
    const removal = libraryRemovalFor(library);

    const onOpen = () => {
      setOpen(true);
      trackingStore.libraryDeleteModalOpened({ libraryId });
    };
    const onClose = () => {
      setOpen(false);
    };
    const onCancel = () => {
      trackingStore.libraryDeleteCancelled({ libraryId });
      onClose();
    };

    const deleteLibrary = async () => {
      try {
        setIsDeleting(true);
        await libraryManager.deleteLibrary(libraryId);
        showToast(removal === 'delete' ? 'library-delete-success' : 'library-detach-success');
        onClose();
        setIsDeleting(false);
        // If we have just deleted this library, then we have to return to the libraries page
        // Extract library ID from pathname (handles slug-based URLs like /library/id-slug)
        const libraryMatch = location.pathname.match(/^\/library\/(.+?)(?:\/|$)/);
        if (libraryMatch) {
          const currentLibraryId = extractObjectId(libraryMatch[1]);
          if (currentLibraryId === libraryId) {
            navigateTo('/libraries');
          }
        }
      } catch (error) {
        showToast(removal === 'delete' ? 'library-delete-failed' : 'library-detach-failed', {
          error,
        });
        setIsDeleting(false);
      }
    };

    useImperativeHandle(ref, () => ({
      open: onOpen,
      close: onClose,
    }));

    return (
      <BaseModal
        open={open}
        onClose={onCancel}
        className={classNames(shell.modal, shell.narrow)}
        title={removal === 'delete' ? 'Delete this library?' : 'Remove this library?'}
      >
        <LibraryDeleteForm
          removal={removal}
          currentName={currentName}
          mediaCount={library?.stats?.count}
          ownerName={library?.otherOwnerName}
          isDeleting={isDeleting}
          onConfirm={deleteLibrary}
          onCancel={onCancel}
        />
      </BaseModal>
    );
  }),
);
